// Average savings, retirement, and financial data by US state
// Sources: Federal Reserve SCF, LIMRA, Bankrate, Census Bureau ACS
// All values are estimates/averages based on available data circa 2023-2024

const savingsByState = {
  alabama: {
    name: 'Alabama',
    abbr: 'AL',
    slug: 'alabama',
    avgSavingsRate: 5.2,
    avgRetirementSavings: 89000,
    avgCreditCardDebt: 5100,
    avgHouseholdIncome: 57000,
    avgEmergencyFund: 6200,
    costOfLivingIndex: 89,  // 100 = US average
    rank: 42,               // ranked by avg retirement savings
    context: 'Alabama residents have a lower-than-average savings rate but benefit from one of the lowest costs of living in the US, making each saved dollar go further.',
  },
  alaska: {
    name: 'Alaska',
    abbr: 'AK',
    slug: 'alaska',
    avgSavingsRate: 7.8,
    avgRetirementSavings: 142000,
    avgCreditCardDebt: 6800,
    avgHouseholdIncome: 82000,
    avgEmergencyFund: 11200,
    costOfLivingIndex: 131,
    rank: 14,
    context: 'Alaska\'s high incomes (boosted by the oil industry and the Permanent Fund Dividend) drive stronger savings despite a significantly higher cost of living.',
  },
  arizona: {
    name: 'Arizona',
    abbr: 'AZ',
    slug: 'arizona',
    avgSavingsRate: 5.8,
    avgRetirementSavings: 105000,
    avgCreditCardDebt: 5600,
    avgHouseholdIncome: 65000,
    avgEmergencyFund: 8100,
    costOfLivingIndex: 102,
    rank: 32,
    context: 'Arizona\'s growth as a retirement destination has created strong financial planning culture, though rapid population growth is pushing housing costs and living expenses up.',
  },
  arkansas: {
    name: 'Arkansas',
    abbr: 'AR',
    slug: 'arkansas',
    avgSavingsRate: 4.8,
    avgRetirementSavings: 81000,
    avgCreditCardDebt: 4900,
    avgHouseholdIncome: 52000,
    avgEmergencyFund: 5800,
    costOfLivingIndex: 88,
    rank: 46,
    context: 'Arkansas has among the lowest incomes and savings rates in the country, but very low housing costs and living expenses soften the impact on financial security.',
  },
  california: {
    name: 'California',
    abbr: 'CA',
    slug: 'california',
    avgSavingsRate: 7.2,
    avgRetirementSavings: 185000,
    avgCreditCardDebt: 7200,
    avgHouseholdIncome: 91000,
    avgEmergencyFund: 14500,
    costOfLivingIndex: 151,
    rank: 4,
    context: 'California leads in high-income earners and tech wealth, driving above-average retirement savings. However, sky-high housing costs mean many Californians struggle to save despite good incomes.',
  },
  colorado: {
    name: 'Colorado',
    abbr: 'CO',
    slug: 'colorado',
    avgSavingsRate: 7.5,
    avgRetirementSavings: 158000,
    avgCreditCardDebt: 5900,
    avgHouseholdIncome: 85000,
    avgEmergencyFund: 12800,
    costOfLivingIndex: 118,
    rank: 9,
    context: 'Colorado has a highly educated, high-income workforce and strong savings culture. Denver\'s tech and aerospace sectors drive above-average compensation and retirement contributions.',
  },
  connecticut: {
    name: 'Connecticut',
    abbr: 'CT',
    slug: 'connecticut',
    avgSavingsRate: 8.1,
    avgRetirementSavings: 210000,
    avgCreditCardDebt: 6500,
    avgHouseholdIncome: 95000,
    avgEmergencyFund: 16200,
    costOfLivingIndex: 131,
    rank: 1,
    context: 'Connecticut is one of the wealthiest states, driven by finance and insurance industries. High incomes translate to higher savings, though the cost of living is among the highest in the nation.',
  },
  delaware: {
    name: 'Delaware',
    abbr: 'DE',
    slug: 'delaware',
    avgSavingsRate: 6.9,
    avgRetirementSavings: 138000,
    avgCreditCardDebt: 5800,
    avgHouseholdIncome: 76000,
    avgEmergencyFund: 10500,
    costOfLivingIndex: 109,
    rank: 16,
    context: 'Delaware\'s corporate and financial services concentration creates above-average incomes. No state sales tax makes consumer spending more efficient, leaving more for savings.',
  },
  florida: {
    name: 'Florida',
    abbr: 'FL',
    slug: 'florida',
    avgSavingsRate: 5.5,
    avgRetirementSavings: 118000,
    avgCreditCardDebt: 6100,
    avgHouseholdIncome: 67000,
    avgEmergencyFund: 8900,
    costOfLivingIndex: 103,
    rank: 26,
    context: 'Florida\'s large retiree population boosts average retirement savings figures. No state income tax is attractive, though property insurance and hurricane costs create unexpected financial pressures.',
  },
  georgia: {
    name: 'Georgia',
    abbr: 'GA',
    slug: 'georgia',
    avgSavingsRate: 5.7,
    avgRetirementSavings: 101000,
    avgCreditCardDebt: 5800,
    avgHouseholdIncome: 66000,
    avgEmergencyFund: 7900,
    costOfLivingIndex: 96,
    rank: 35,
    context: 'Atlanta\'s tech hub and financial sector are lifting Georgia\'s average incomes, though rural-urban income gaps are significant. Savings rates are near the national average.',
  },
  hawaii: {
    name: 'Hawaii',
    abbr: 'HI',
    slug: 'hawaii',
    avgSavingsRate: 6.3,
    avgRetirementSavings: 130000,
    avgCreditCardDebt: 5400,
    avgHouseholdIncome: 88000,
    avgEmergencyFund: 9800,
    costOfLivingIndex: 193,
    rank: 20,
    context: 'Hawaii has high incomes but also the highest cost of living in the US. After housing and food costs, many Hawaii residents have little left to save despite decent wages.',
  },
  idaho: {
    name: 'Idaho',
    abbr: 'ID',
    slug: 'idaho',
    avgSavingsRate: 6.4,
    avgRetirementSavings: 102000,
    avgCreditCardDebt: 5100,
    avgHouseholdIncome: 65000,
    avgEmergencyFund: 8200,
    costOfLivingIndex: 105,
    rank: 34,
    context: 'Idaho has seen rapid population growth, especially in the Boise area, attracting tech workers and remote workers. Savings rates are slightly above average for the Mountain West.',
  },
  illinois: {
    name: 'Illinois',
    abbr: 'IL',
    slug: 'illinois',
    avgSavingsRate: 6.1,
    avgRetirementSavings: 128000,
    avgCreditCardDebt: 6200,
    avgHouseholdIncome: 74000,
    avgEmergencyFund: 9500,
    costOfLivingIndex: 101,
    rank: 21,
    context: 'Chicago\'s financial and professional services drive strong income and savings in northern Illinois. The state\'s pension crisis has created uncertainty for public sector workers\' retirement.',
  },
  indiana: {
    name: 'Indiana',
    abbr: 'IN',
    slug: 'indiana',
    avgSavingsRate: 5.6,
    avgRetirementSavings: 96000,
    avgCreditCardDebt: 5300,
    avgHouseholdIncome: 62000,
    avgEmergencyFund: 7200,
    costOfLivingIndex: 91,
    rank: 37,
    context: 'Indiana\'s manufacturing base provides steady employment but moderate wages. Low cost of living helps offset below-average savings rates.',
  },
  iowa: {
    name: 'Iowa',
    abbr: 'IA',
    slug: 'iowa',
    avgSavingsRate: 6.3,
    avgRetirementSavings: 108000,
    avgCreditCardDebt: 5000,
    avgHouseholdIncome: 65000,
    avgEmergencyFund: 8400,
    costOfLivingIndex: 89,
    rank: 29,
    context: 'Iowa consistently ranks well for financial health. Residents tend to carry less debt than the national average, and low housing costs allow for solid savings rates.',
  },
  kansas: {
    name: 'Kansas',
    abbr: 'KS',
    slug: 'kansas',
    avgSavingsRate: 5.9,
    avgRetirementSavings: 103000,
    avgCreditCardDebt: 5200,
    avgHouseholdIncome: 63000,
    avgEmergencyFund: 7800,
    costOfLivingIndex: 90,
    rank: 33,
    context: 'Kansas has a stable agricultural and manufacturing economy. Low cost of living helps residents save despite moderate incomes.',
  },
  kentucky: {
    name: 'Kentucky',
    abbr: 'KY',
    slug: 'kentucky',
    avgSavingsRate: 4.9,
    avgRetirementSavings: 84000,
    avgCreditCardDebt: 5000,
    avgHouseholdIncome: 56000,
    avgEmergencyFund: 6100,
    costOfLivingIndex: 90,
    rank: 44,
    context: 'Kentucky faces economic challenges with lower-than-average incomes and savings rates. Healthcare costs and low wage growth are the primary barriers to building retirement savings.',
  },
  louisiana: {
    name: 'Louisiana',
    abbr: 'LA',
    slug: 'louisiana',
    avgSavingsRate: 4.6,
    avgRetirementSavings: 79000,
    avgCreditCardDebt: 5300,
    avgHouseholdIncome: 55000,
    avgEmergencyFund: 5500,
    costOfLivingIndex: 93,
    rank: 47,
    context: 'Louisiana has among the lowest savings rates and retirement savings in the US. Oil and gas sector volatility creates income uncertainty for many households.',
  },
  maine: {
    name: 'Maine',
    abbr: 'ME',
    slug: 'maine',
    avgSavingsRate: 6.0,
    avgRetirementSavings: 105000,
    avgCreditCardDebt: 5200,
    avgHouseholdIncome: 63000,
    avgEmergencyFund: 7900,
    costOfLivingIndex: 110,
    rank: 31,
    context: 'Maine has an aging population and above-average retirement savings despite moderate incomes. The tourism and healthcare sectors provide steady employment.',
  },
  maryland: {
    name: 'Maryland',
    abbr: 'MD',
    slug: 'maryland',
    avgSavingsRate: 7.8,
    avgRetirementSavings: 175000,
    avgCreditCardDebt: 6300,
    avgHouseholdIncome: 98000,
    avgEmergencyFund: 13500,
    costOfLivingIndex: 125,
    rank: 5,
    context: 'Maryland has the highest median household income of any state, driven by the high concentration of federal government workers and defense contractors in the DC metro area.',
  },
  massachusetts: {
    name: 'Massachusetts',
    abbr: 'MA',
    slug: 'massachusetts',
    avgSavingsRate: 8.2,
    avgRetirementSavings: 195000,
    avgCreditCardDebt: 5900,
    avgHouseholdIncome: 94000,
    avgEmergencyFund: 15400,
    costOfLivingIndex: 147,
    rank: 2,
    context: 'Massachusetts\' education, biotech, and technology industries create high incomes and sophisticated financial planning. Boston has one of the highest concentrations of financial advisors per capita.',
  },
  michigan: {
    name: 'Michigan',
    abbr: 'MI',
    slug: 'michigan',
    avgSavingsRate: 5.5,
    avgRetirementSavings: 98000,
    avgCreditCardDebt: 5500,
    avgHouseholdIncome: 64000,
    avgEmergencyFund: 7400,
    costOfLivingIndex: 92,
    rank: 36,
    context: 'Michigan\'s auto industry has long provided strong union wages and pension benefits, though the shift to EVs is creating uncertainty for many autoworkers\' retirement plans.',
  },
  minnesota: {
    name: 'Minnesota',
    abbr: 'MN',
    slug: 'minnesota',
    avgSavingsRate: 7.3,
    avgRetirementSavings: 148000,
    avgCreditCardDebt: 5400,
    avgHouseholdIncome: 80000,
    avgEmergencyFund: 11800,
    costOfLivingIndex: 105,
    rank: 12,
    context: 'Minnesota consistently ranks among the best states for financial health. Strong education levels, corporate headquarters (Target, 3M, UnitedHealth), and a savings-focused culture drive excellent outcomes.',
  },
  mississippi: {
    name: 'Mississippi',
    abbr: 'MS',
    slug: 'mississippi',
    avgSavingsRate: 4.2,
    avgRetirementSavings: 70000,
    avgCreditCardDebt: 4800,
    avgHouseholdIncome: 48000,
    avgEmergencyFund: 4900,
    costOfLivingIndex: 85,
    rank: 50,
    context: 'Mississippi consistently ranks last or near-last for savings and income. The state\'s low median income is the primary driver, though extremely low housing costs partially offset this.',
  },
  missouri: {
    name: 'Missouri',
    abbr: 'MO',
    slug: 'missouri',
    avgSavingsRate: 5.7,
    avgRetirementSavings: 100000,
    avgCreditCardDebt: 5400,
    avgHouseholdIncome: 62000,
    avgEmergencyFund: 7600,
    costOfLivingIndex: 90,
    rank: 38,
    context: 'Missouri has a diverse economy with healthcare, finance, and agriculture. Kansas City and St. Louis residents outpace rural Missouri in savings rates significantly.',
  },
  montana: {
    name: 'Montana',
    abbr: 'MT',
    slug: 'montana',
    avgSavingsRate: 5.8,
    avgRetirementSavings: 95000,
    avgCreditCardDebt: 5000,
    avgHouseholdIncome: 60000,
    avgEmergencyFund: 7100,
    costOfLivingIndex: 103,
    rank: 39,
    context: 'Montana\'s rugged individualism extends to personal finance — residents tend to have modest but consistent savings habits. Remote work migration is boosting incomes in recent years.',
  },
  nebraska: {
    name: 'Nebraska',
    abbr: 'NE',
    slug: 'nebraska',
    avgSavingsRate: 6.4,
    avgRetirementSavings: 110000,
    avgCreditCardDebt: 5100,
    avgHouseholdIncome: 67000,
    avgEmergencyFund: 8600,
    costOfLivingIndex: 93,
    rank: 27,
    context: 'Nebraska has strong financial literacy and above-average savings rates. Omaha\'s financial and insurance industries (including Berkshire Hathaway) create strong financial planning culture.',
  },
  nevada: {
    name: 'Nevada',
    abbr: 'NV',
    slug: 'nevada',
    avgSavingsRate: 4.9,
    avgRetirementSavings: 88000,
    avgCreditCardDebt: 6400,
    avgHouseholdIncome: 65000,
    avgEmergencyFund: 6500,
    costOfLivingIndex: 110,
    rank: 43,
    context: 'Nevada\'s hospitality-heavy economy creates variable income and below-average retirement savings. No state income tax helps, but high consumer debt relative to savings is a concern.',
  },
  'new-hampshire': {
    name: 'New Hampshire',
    abbr: 'NH',
    slug: 'new-hampshire',
    avgSavingsRate: 7.6,
    avgRetirementSavings: 162000,
    avgCreditCardDebt: 5600,
    avgHouseholdIncome: 88000,
    avgEmergencyFund: 12900,
    costOfLivingIndex: 120,
    rank: 8,
    context: 'New Hampshire has no income tax or sales tax, which combined with high incomes makes it one of the better states for wealth accumulation. Strong tech and healthcare employment drives savings.',
  },
  'new-jersey': {
    name: 'New Jersey',
    abbr: 'NJ',
    slug: 'new-jersey',
    avgSavingsRate: 7.4,
    avgRetirementSavings: 180000,
    avgCreditCardDebt: 6800,
    avgHouseholdIncome: 96000,
    avgEmergencyFund: 13900,
    costOfLivingIndex: 130,
    rank: 6,
    context: 'New Jersey\'s proximity to New York City creates very high incomes, especially in finance and pharma. Despite very high property taxes and cost of living, retirement savings rates are excellent.',
  },
  'new-mexico': {
    name: 'New Mexico',
    abbr: 'NM',
    slug: 'new-mexico',
    avgSavingsRate: 4.5,
    avgRetirementSavings: 78000,
    avgCreditCardDebt: 5100,
    avgHouseholdIncome: 53000,
    avgEmergencyFund: 5600,
    costOfLivingIndex: 91,
    rank: 49,
    context: 'New Mexico faces challenges with high poverty rates and lower-than-average incomes. Government employment (federal agencies, labs) provides stability for a subset of the workforce.',
  },
  'new-york': {
    name: 'New York',
    abbr: 'NY',
    slug: 'new-york',
    avgSavingsRate: 7.0,
    avgRetirementSavings: 168000,
    avgCreditCardDebt: 7000,
    avgHouseholdIncome: 82000,
    avgEmergencyFund: 12100,
    costOfLivingIndex: 139,
    rank: 7,
    context: 'New York City\'s financial sector drives stratospheric incomes at the top, but the median New Yorker battles very high housing costs, taxes, and living expenses that limit savings.',
  },
  'north-carolina': {
    name: 'North Carolina',
    abbr: 'NC',
    slug: 'north-carolina',
    avgSavingsRate: 6.0,
    avgRetirementSavings: 108000,
    avgCreditCardDebt: 5500,
    avgHouseholdIncome: 68000,
    avgEmergencyFund: 8200,
    costOfLivingIndex: 98,
    rank: 28,
    context: 'North Carolina\'s Research Triangle (Raleigh, Durham, Chapel Hill) has created a thriving tech and pharma economy. Moderate cost of living plus growing incomes is a good savings combination.',
  },
  'north-dakota': {
    name: 'North Dakota',
    abbr: 'ND',
    slug: 'north-dakota',
    avgSavingsRate: 7.1,
    avgRetirementSavings: 125000,
    avgCreditCardDebt: 4800,
    avgHouseholdIncome: 72000,
    avgEmergencyFund: 9500,
    costOfLivingIndex: 100,
    rank: 22,
    context: 'North Dakota\'s oil and gas wealth has boosted incomes significantly. Residents also carry some of the lowest credit card debt in the country, reflecting conservative financial habits.',
  },
  ohio: {
    name: 'Ohio',
    abbr: 'OH',
    slug: 'ohio',
    avgSavingsRate: 5.6,
    avgRetirementSavings: 99000,
    avgCreditCardDebt: 5400,
    avgHouseholdIncome: 63000,
    avgEmergencyFund: 7300,
    costOfLivingIndex: 92,
    rank: 40,
    context: 'Ohio\'s diverse economy of manufacturing, healthcare, and finance creates a mix of savings outcomes. Columbus\'s growth as a tech hub is boosting incomes in central Ohio.',
  },
  oklahoma: {
    name: 'Oklahoma',
    abbr: 'OK',
    slug: 'oklahoma',
    avgSavingsRate: 5.0,
    avgRetirementSavings: 87000,
    avgCreditCardDebt: 5200,
    avgHouseholdIncome: 58000,
    avgEmergencyFund: 6300,
    costOfLivingIndex: 89,
    rank: 45,
    context: 'Oklahoma\'s oil-dependent economy creates volatile income cycles. While cost of living is low, savings rates are below average and retirement preparedness is a challenge.',
  },
  oregon: {
    name: 'Oregon',
    abbr: 'OR',
    slug: 'oregon',
    avgSavingsRate: 6.7,
    avgRetirementSavings: 135000,
    avgCreditCardDebt: 5600,
    avgHouseholdIncome: 75000,
    avgEmergencyFund: 10400,
    costOfLivingIndex: 119,
    rank: 17,
    context: 'Oregon\'s tech sector (Intel, Nike, Adidas) and Portland\'s creative economy drive solid incomes. High housing costs in Portland are a growing savings challenge.',
  },
  pennsylvania: {
    name: 'Pennsylvania',
    abbr: 'PA',
    slug: 'pennsylvania',
    avgSavingsRate: 6.2,
    avgRetirementSavings: 122000,
    avgCreditCardDebt: 5700,
    avgHouseholdIncome: 70000,
    avgEmergencyFund: 9100,
    costOfLivingIndex: 101,
    rank: 24,
    context: 'Pennsylvania\'s diverse economy (healthcare, finance, manufacturing) creates moderate savings rates. Philadelphia and Pittsburgh have strong professional services that boost regional averages.',
  },
  'rhode-island': {
    name: 'Rhode Island',
    abbr: 'RI',
    slug: 'rhode-island',
    avgSavingsRate: 6.3,
    avgRetirementSavings: 120000,
    avgCreditCardDebt: 5800,
    avgHouseholdIncome: 73000,
    avgEmergencyFund: 9200,
    costOfLivingIndex: 118,
    rank: 25,
    context: 'Rhode Island has solid retirement savings for a small state. Healthcare, education, and finance are the dominant employers. High cost of living relative to income is a constraint.',
  },
  'south-carolina': {
    name: 'South Carolina',
    abbr: 'SC',
    slug: 'south-carolina',
    avgSavingsRate: 5.3,
    avgRetirementSavings: 91000,
    avgCreditCardDebt: 5300,
    avgHouseholdIncome: 59000,
    avgEmergencyFund: 6700,
    costOfLivingIndex: 94,
    rank: 41,
    context: 'South Carolina is a popular retirement destination, but working-age residents face below-average wages. Manufacturing growth and retiree migration are slowly improving the picture.',
  },
  'south-dakota': {
    name: 'South Dakota',
    abbr: 'SD',
    slug: 'south-dakota',
    avgSavingsRate: 6.5,
    avgRetirementSavings: 113000,
    avgCreditCardDebt: 4900,
    avgHouseholdIncome: 65000,
    avgEmergencyFund: 8800,
    costOfLivingIndex: 93,
    rank: 23,
    context: 'South Dakota has no state income tax and conservative financial habits. Low debt and steady savings rates make it one of the more financially secure smaller states.',
  },
  tennessee: {
    name: 'Tennessee',
    abbr: 'TN',
    slug: 'tennessee',
    avgSavingsRate: 5.5,
    avgRetirementSavings: 97000,
    avgCreditCardDebt: 5500,
    avgHouseholdIncome: 62000,
    avgEmergencyFund: 7100,
    costOfLivingIndex: 91,
    rank: 3,
    context: 'Tennessee has no state income tax, making it attractive for savers. Nashville\'s growth as a healthcare and tech hub is pushing incomes and savings rates upward.',
  },
  texas: {
    name: 'Texas',
    abbr: 'TX',
    slug: 'texas',
    avgSavingsRate: 6.0,
    avgRetirementSavings: 115000,
    avgCreditCardDebt: 6200,
    avgHouseholdIncome: 72000,
    avgEmergencyFund: 8800,
    costOfLivingIndex: 98,
    rank: 19,
    context: 'Texas has no state income tax and a booming economy. Tech, energy, and healthcare drive above-average incomes in Austin, Dallas, and Houston, though property taxes are high.',
  },
  utah: {
    name: 'Utah',
    abbr: 'UT',
    slug: 'utah',
    avgSavingsRate: 7.2,
    avgRetirementSavings: 145000,
    avgCreditCardDebt: 5200,
    avgHouseholdIncome: 82000,
    avgEmergencyFund: 11500,
    costOfLivingIndex: 109,
    rank: 13,
    context: 'Utah consistently ranks among the best states for financial health. Religious tithing culture encourages budgeting, low consumer debt, and strong savings habits.',
  },
  vermont: {
    name: 'Vermont',
    abbr: 'VT',
    slug: 'vermont',
    avgSavingsRate: 6.5,
    avgRetirementSavings: 118000,
    avgCreditCardDebt: 5100,
    avgHouseholdIncome: 68000,
    avgEmergencyFund: 9000,
    costOfLivingIndex: 115,
    rank: 15,
    context: 'Vermont has moderate incomes but strong savings habits. Education, healthcare, and tourism are the main industries. High housing costs for a rural state are a growing concern.',
  },
  virginia: {
    name: 'Virginia',
    abbr: 'VA',
    slug: 'virginia',
    avgSavingsRate: 7.6,
    avgRetirementSavings: 165000,
    avgCreditCardDebt: 6100,
    avgHouseholdIncome: 89000,
    avgEmergencyFund: 12800,
    costOfLivingIndex: 113,
    rank: 10,
    context: 'Northern Virginia\'s concentration of federal contractors and tech companies creates very high incomes. The DC metro area\'s government-adjacent economy provides stability and strong savings potential.',
  },
  washington: {
    name: 'Washington',
    abbr: 'WA',
    slug: 'washington',
    avgSavingsRate: 8.0,
    avgRetirementSavings: 175000,
    avgCreditCardDebt: 5800,
    avgHouseholdIncome: 94000,
    avgEmergencyFund: 14200,
    costOfLivingIndex: 128,
    rank: 11,
    context: 'Washington has no state income tax and a tech-dominated economy (Amazon, Microsoft, Boeing). Seattle\'s tech wealth drives some of the highest household incomes and retirement savings in the country.',
  },
  'west-virginia': {
    name: 'West Virginia',
    abbr: 'WV',
    slug: 'west-virginia',
    avgSavingsRate: 4.3,
    avgRetirementSavings: 72000,
    avgCreditCardDebt: 4800,
    avgHouseholdIncome: 50000,
    avgEmergencyFund: 5100,
    costOfLivingIndex: 87,
    rank: 48,
    context: 'West Virginia faces persistent economic challenges with the coal industry\'s decline. Very low incomes make it difficult to save despite one of the lowest costs of living in the country.',
  },
  wisconsin: {
    name: 'Wisconsin',
    abbr: 'WI',
    slug: 'wisconsin',
    avgSavingsRate: 6.3,
    avgRetirementSavings: 115000,
    avgCreditCardDebt: 5200,
    avgHouseholdIncome: 68000,
    avgEmergencyFund: 8700,
    costOfLivingIndex: 95,
    rank: 30,
    context: 'Wisconsin has a strong manufacturing and agriculture base with solid savings rates. Madison\'s tech and university economy is boosting younger workers\' incomes and savings.',
  },
  wyoming: {
    name: 'Wyoming',
    abbr: 'WY',
    slug: 'wyoming',
    avgSavingsRate: 6.9,
    avgRetirementSavings: 128000,
    avgCreditCardDebt: 5000,
    avgHouseholdIncome: 68000,
    avgEmergencyFund: 9700,
    costOfLivingIndex: 96,
    rank: 18,
    context: 'Wyoming has no state income tax and benefits from mineral royalties. Low population and a resource-based economy create moderate but stable savings outcomes.',
  },
};

export function getSavingsByState(slug) {
  return savingsByState[slug] || null;
}

export const ALL_STATE_SLUGS = Object.keys(savingsByState);
export const ALL_STATES = Object.values(savingsByState);
export default savingsByState;
